// dashboard/src/components/EditProjectModal.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Select from 'react-select';
import { updateProject, fetchDonors, fetchProgramManagers } from '../api/projectCreationApi';
import { toast } from 'react-toastify';
import { parseISO, addDays, isWeekend, isAfter, format } from 'date-fns';
import { FaBriefcase, FaMapPin, FaFileContract, FaTag, FaCalendar, FaUser, FaCaretDown, FaCaretUp, FaTimes, FaDollarSign } from 'react-icons/fa';
import axios from 'axios';

const Section = ({ title, icon, children, defaultOpen = true }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden bg-white">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left px-5 py-3 bg-gray-50 font-semibold text-gray-700 flex justify-between items-center hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center space-x-2">
                    <span className="text-orange-500">{icon}</span>
                    <span>{title}</span>
                </div>
                <span className="text-gray-500 text-lg">{isOpen ? <FaCaretDown /> : <FaCaretUp />}</span>
            </button>
            {isOpen && <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">{children}</div>}
        </div>
    );
};

const FormField = ({ label, name, value, onChange, type = "text", placeholder = "", className = "", options = null, icon, ...props }) => (
    <div className={className}>
        <label htmlFor={name} className="block mb-1 font-medium text-gray-700">
            {label} {props.required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
            {icon && (
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {icon}
                </div>
            )}
            {type === 'select' ? (
                <select id={name} name={name} value={value} onChange={onChange} className={`w-full border border-gray-300 rounded-md px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 ${icon ? 'pl-10' : ''}`}>
                    {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            ) : type === 'textarea' ? (
                <textarea id={name} name={name} value={value} onChange={onChange} placeholder={placeholder} rows="3" className={`w-full border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 ${icon ? 'pl-10' : ''} `} {...props} />
            ) : (
                <input id={name} name={name} type={type} value={value} onChange={onChange} placeholder={placeholder} className={`w-full border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 ${icon ? 'pl-10' : ''} `} {...props} />
            )}
        </div>
    </div>
);

const calculateEndDate = (start, days) => {
    if (!start || !days) return '';
    let date = parseISO(start);
    let workdays = 0;
    while (workdays < days) {
        date = addDays(date, 1);
        if (!isWeekend(date)) {
            workdays++;
        }
    }
    return date;
};

const calculateDuration = (start, end) => {
    if (!start || !end || isAfter(parseISO(start), parseISO(end))) return '';
    let date = parseISO(start);
    let workdays = 0;
    while (isAfter(parseISO(end), date)) {
        if (!isWeekend(date)) {
            workdays++;
        }
        date = addDays(date, 1);
    }
    return workdays;
};

const EditProjectModal = ({ isOpen, onClose, projectId, onSuccess }) => {
    const [formData, setFormData] = useState(null);
    const [donors, setDonors] = useState([]);
    const [programManagers, setProgramManagers] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [fetching, setFetching] = useState(true);
    const modalRef = useRef(null);

    // Handle click outside modal to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);

    const fetchProjectDetails = useCallback(async () => {
        if (!projectId) {
            setFetching(false);
            return;
        }

        setFetching(true);
        try {
            const API_URL = "https://ims.holdthechild.net";
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Authentication token not found.");

            const response = await axios.get(`${API_URL}/api/projects/${projectId}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });
            const projectData = response.data.data;

            setFormData({
                ...projectData, // Spreads all fields including budgetCode and projectBudget
                projectCategories: projectData.projectCategories?.join(', ') || '',
                startDate: format(new Date(projectData.startDate), 'yyyy-MM-dd'),
                endDate: format(new Date(projectData.endDate), 'yyyy-MM-dd'),
                donor: projectData.donor?.map(d => d._id) || [],
                assignedProgramManager: projectData.assignedProgramManager?._id || '',
            });
        } catch (err) {
            toast.error("Failed to fetch project details.");
            console.error(err);
            setFormData(null);
        } finally {
            setFetching(false);
        }
    }, [projectId]);

    const loadDonorsAndManagers = useCallback(async () => {
        try {
            const [donorsData, managersData] = await Promise.all([
                fetchDonors(),
                fetchProgramManagers(),
            ]);
            setDonors(donorsData);
            setProgramManagers(managersData);
        } catch (error) {
            toast.error("Failed to load form data.");
            console.error(error);
            setProgramManagers([]);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchProjectDetails();
            loadDonorsAndManagers();
        }
    }, [isOpen, fetchProjectDetails, loadDonorsAndManagers]);

    const donorOptions = useMemo(() => {
        if (!donors) return [];
        return donors.map(donor => ({
            value: donor._id,
            label: donor.displayName || donor.name || donor.organization,
        }));
    }, [donors]);

    const programManagerOptions = useMemo(() => {
        if (!Array.isArray(programManagers)) {
            console.warn("programManagers is not an array, returning empty options.", programManagers);
            return [];
        }
        return programManagers.map(manager => ({
            value: manager._id,
            label: `${manager.firstName} ${manager.lastName}`,
        }));
    }, [programManagers]);

    const statusOptions = [
        { value: 'Planning', label: 'Planning' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'On Hold', label: 'On Hold' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' },
        { value: 'Archived', label: 'Archived' },
    ];

    const orangeTheme = theme => ({
        ...theme,
        colors: {
            ...theme.colors,
            primary25: '#FFF5E6',
            primary: '#F68B1F',
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const newForm = { ...prev, [name]: value };

            const parsedStartDate = newForm.startDate ? new Date(newForm.startDate) : null;
            const parsedEndDate = newForm.endDate ? new Date(newForm.endDate) : null;
            const parsedDuration = parseInt(newForm.duration, 10);

            if (name === 'startDate' && parsedStartDate && parsedDuration) {
                const calculatedEndDate = calculateEndDate(newForm.startDate, parsedDuration);
                newForm.endDate = format(calculatedEndDate, 'yyyy-MM-dd');
            } else if (name === 'duration' && parsedStartDate && parsedDuration) {
                const calculatedEndDate = calculateEndDate(newForm.startDate, parsedDuration);
                newForm.endDate = format(calculatedEndDate, 'yyyy-MM-dd');
            } else if (name === 'endDate' && parsedStartDate && parsedEndDate && parsedEndDate >= parsedStartDate) {
                const calculatedDuration = calculateDuration(newForm.startDate, newForm.endDate);
                newForm.duration = calculatedDuration;
            } else if (name === 'startDate' && parsedStartDate && parsedEndDate && parsedEndDate >= parsedStartDate) {
                const calculatedDuration = calculateDuration(newForm.startDate, newForm.endDate);
                newForm.duration = calculatedDuration;
            }

            return newForm;
        });
    };
    const handleSelectChange = (selectedOptions, { name }) => {
        if (name === 'donor') {
            setFormData(prev => ({
                ...prev,
                [name]: selectedOptions ? selectedOptions.map(opt => opt.value) : [],
            }));
        } else if (name === 'status') {
            setFormData(prev => ({
                ...prev,
                [name]: selectedOptions ? selectedOptions.value : '',
            }));
        } else if (name === 'assignedProgramManager') {
            setFormData(prev => ({
                ...prev,
                [name]: selectedOptions ? selectedOptions.value : '',
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        if (new Date(formData.endDate) < new Date(formData.startDate)) {
            toast.error("End date cannot be before the start date.");
            setIsSaving(false);
            return;
        }

        const { _id, ...updateData } = formData;

        const payload = {
            ...updateData,
            projectCategories: updateData.projectCategories
                .split(',')
                .map((cat) => cat.trim())
                .filter((cat) => cat !== ''),
            // Ensure budget is sent as a number
            projectBudget: parseFloat(updateData.projectBudget) || 0,
            duration: undefined, // Backend recalculates this
        };

        // console.log("Submitting payload:", payload); // Debugging

        try {
            await updateProject(projectId, payload);
            toast.success('Project updated successfully!');
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Update project error:", err);

            if (err.response) {
                console.error("Server responded with:", err.response.status, err.response.data);
                const errorMessage = err.response.data?.message || 'Failed to update project.';
                toast.error(errorMessage);
            } else {
                toast.error('Failed to update project. Check your network connection.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    if (fetching) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
                <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                    <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-50">
                        <div className="flex items-center justify-between px-8 py-6">
                            <div className="flex items-center space-x-4">
                                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                                    <FaBriefcase className="w-7 h-7 text-black" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-black">Edit Project Profile</h2>
                                    <p className="text-black-100 mt-1">Project Management System</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
                            >
                                <FaTimes className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                        <svg className="animate-spin h-8 w-8 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="mt-3 text-lg font-semibold">Loading project details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!formData) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header with Orange Gradient */}
                <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-50">
                    <div className="flex items-center justify-between px-8 py-6">
                        <div className="flex items-center space-x-4">
                            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                                <FaBriefcase className="w-7 h-7 text-black" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-black">Edit Project Profile</h2>
                                <p className="text-black-100 mt-1">Project Management System</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
                        >
                            <FaTimes className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-grow px-8 py-6 overflow-y-auto">
                    <form id="edit-project-form" onSubmit={handleSubmit} className="space-y-6">
                        <Section title="Project Details" icon={<FaBriefcase />}>
                            <FormField 
                                label="Project Title" 
                                name="projectTitle" 
                                value={formData.projectTitle || ''} 
                                onChange={handleChange} 
                                required 
                                icon={<FaBriefcase className="h-4 w-4 text-gray-400" />}
                            />
                            <FormField 
                                label="Location" 
                                name="location" 
                                value={formData.location || ''} 
                                onChange={handleChange} 
                                required 
                                icon={<FaMapPin className="h-4 w-4 text-gray-400" />}
                            />
                            <FormField 
                                label="Project Categories" 
                                name="projectCategories" 
                                value={formData.projectCategories || ''} 
                                onChange={handleChange} 
                                placeholder="e.g., Education, Health" 
                                icon={<FaTag className="h-4 w-4 text-gray-400" />}
                            />
                            <div className="sm:col-span-2">
                                <FormField 
                                    label="Description" 
                                    name="description" 
                                    type="textarea" 
                                    value={formData.description || ''} 
                                    onChange={handleChange} 
                                    required 
                                    icon={<FaFileContract className="h-4 w-4 text-gray-400" />}
                                />
                            </div>
                        </Section>
                        
                        {/* ✨ NEW: Financial Information Section */}
                        <Section title="Financial Information" icon={<FaDollarSign />}>
                             <FormField 
                                label="Budget Code" 
                                name="budgetCode" 
                                value={formData.budgetCode || ''} 
                                onChange={handleChange} 
                                placeholder="Auto-generated" 
                                readOnly
                                className="bg-gray-100 text-gray-600 cursor-not-allowed"
                                icon={<FaTag className="h-4 w-4 text-gray-400" />}
                            />
                            <FormField 
                                label="Project Budget (USD)" 
                                name="projectBudget" 
                                type="number" 
                                min="0"
                                step="0.01"
                                value={formData.projectBudget || ''} 
                                onChange={handleChange} 
                                required
                                placeholder="0.00"
                                icon={<FaDollarSign className="h-4 w-4 text-gray-400" />}
                            />
                        </Section>

                        <Section title="Dates & Duration" icon={<FaCalendar />}>
                            <FormField 
                                label="Start Date" 
                                name="startDate" 
                                type="date" 
                                value={formData.startDate || ''} 
                                onChange={handleChange} 
                                required 
                                icon={<FaCalendar className="h-4 w-4 text-gray-400" />}
                            />
                            <FormField 
                                label="End Date" 
                                name="endDate" 
                                type="date" 
                                value={formData.endDate || ''} 
                                onChange={handleChange} 
                                required 
                                min={formData.startDate} 
                                icon={<FaCalendar className="h-4 w-4 text-gray-400" />}
                            />
                            <FormField 
                                label="Duration (working days)" 
                                name="duration" 
                                type="number" 
                                min="1" 
                                value={formData.duration || ''} 
                                onChange={handleChange} 
                                placeholder="Auto-calculated" 
                                icon={<FaCalendar className="h-4 w-4 text-gray-400" />}
                            />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status <span className="text-red-500">*</span></label>
                                <Select
                                    name="status"
                                    options={statusOptions}
                                    value={statusOptions.find(opt => opt.value === formData.status)}
                                    onChange={handleSelectChange}
                                    className="basic-single-select"
                                    classNamePrefix="select"
                                    placeholder="Select a status"
                                    isSearchable={false}
                                    theme={orangeTheme}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Program Manager</label>
                                <Select
                                    name="assignedProgramManager"
                                    options={programManagerOptions}
                                    value={programManagerOptions.find(opt => opt.value === formData.assignedProgramManager)}
                                    onChange={handleSelectChange}
                                    className="basic-single-select"
                                    classNamePrefix="select"
                                    placeholder="Select a program manager (optional)"
                                    isClearable
                                    theme={orangeTheme}
                                />
                            </div>
                        </Section>

                        <Section title="Donor Information" icon={<FaUser />}>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Donor(s) <span className="text-red-500">*</span></label>
                                <Select
                                    name="donor"
                                    options={donorOptions}
                                    value={donorOptions.filter(opt => formData.donor.includes(opt.value))}
                                    onChange={handleSelectChange}
                                    isMulti
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    placeholder="Select donor(s)"
                                    theme={orangeTheme}
                                />
                            </div>
                        </Section>
                    </form>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
                    <div className="flex justify-end gap-4">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            disabled={isSaving} 
                            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            form="edit-project-form" 
                            disabled={isSaving} 
                            className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                                isSaving 
                                    ? 'bg-orange-400 cursor-not-allowed' 
                                    : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl'
                            }`}
                        >
                            {isSaving ? (
                                <div className="flex items-center space-x-3">
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Saving Changes...</span>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2">
                                    <FaBriefcase />
                                    <span>Save Changes</span>
                                </div>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProjectModal;